import { ZodIssueCode, z } from 'zod';
import { EmptyValidationMessage, FieldValues, Path, mapTextField } from '@ninja/utilities';
import { CommonRegex } from '../../../../../common';
import { useAppPlatformContextOptions } from '../../../../../contexts/app';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';

const field = 'emiratesIdNumber';

export const useEmiratesIdField = <TForm extends FieldValues>() => {
	const { translate: t } = useAppPlatformContextOptions();
	const getField = ({ field }: { field: Path<TForm> }) =>
		mapTextField({
			field,
			inputMode: 'numeric',
			label: t('members:fields.emirate-id.label'),
			suffix: '784 -',
			placeholder: t('members:fields.emirate-id.placeholder'),
			directionForInput: 'ltr',
			placeholderDir: 'ltr',
			mask: '9999 - 9999999 - 9',
			onPaste: (value: string) => {
				const pastedValue = value.replace(/-|\s/g, '');
				const finalValue = pastedValue.startsWith('784') ? pastedValue.substring(3) : pastedValue;

				return finalValue;
			},
		});
	const getValidation = () => z.string({ required_error: EmptyValidationMessage }).optional();
	const validate = (
		value: string | undefined,
		addFieldError: ReturnType<typeof prepareFieldError>,
		yearOfBirth?: string,
		ctx?: z.RefinementCtx
	) => {
		if (!value) {
			addFieldError({ field, message: EmptyValidationMessage });

			return;
		}

		if (value.length < 12) {
			addFieldError({ field, message: t('members:fields.emirate-id.validation.regex') });

			return;
		}

		if ((yearOfBirth && !value.startsWith(yearOfBirth)) || !CommonRegex.emiratesId.test(value)) {
			ctx?.addIssue({
				code: ZodIssueCode.invalid_enum_value,
				received: field,
				path: [field],
				message: '',
				options: [],
			});

			return;
		}
	};

	return {
		getField,
		getValidation,
		validate,
	};
};
